import React, { useState, useEffect, useCallback } from 'react';
import Container from '@mui/material/Container';
import PermissionBasedGuard from 'src/auth/guard/permission-based-guard';
import { useAuthContext } from 'src/auth/hooks';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import { useGetSelectedFoodsCustomers, useGetSelectedFoodCustomerDetails, useGetRestaurantSelectedFoodsCustomers } from 'src/api/restaurant';
import {
  TableCell,
  TableRow,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import { components } from 'src/types/schema';
import Iconify from 'src/components/iconify';
import { DataTable, TableToolbar, useFilters, usePagination, Column } from './selected-food-table';

// ----------------------------------------------------------------------

type CustomerData = components['schemas']['PartnersSubscriptionSelectedFoodCustomerDateData'];
type PlanGroup = components['schemas']['PartnersSubscriptionSelectedFoodCustomerDateDataPlanGroup'];
type Subscription = components['schemas']['PartnersSubscriptionSelectedFoodCustomerDateDataPlanGroupSubscription'];
type SelectedFood = components['schemas']['PartnersSelectedFoodSubscripitonCustomerSubscriptionSelectedFood'];
type CustomerDetail = components['schemas']['PartnersSubscriptionSelectedFoodCustomerDetails'];

// ----------------------------------------------------------------------

// Updated type for grouped subscription data
interface SubscriptionMealData {
  id: number;
  small_id: string;
  phone_number: string;
  delivery_time: string;
  restaurant_note: string;
  delivery_note: string | null;
  plan_protein_size: number;
  plan_carb_size: number;
  plan_tag: string;
  [mealKey: string]: string | number | null;
}

// ----------------------------------------------------------------------

export default function SelectedFoodCustomersListPage({ restaurantId }: { restaurantId?: string }) {
  const { user } = useAuthContext();
  const { filters, onFilters, onMultipleFilters, resetFilters, getDateRange } = useFilters('selected_food_customers');
  const { page, rowsPerPage, onPageChange, onRowsPerPageChange } = usePagination();
  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState<{ id: number, date: string } | null>(null);

  const dateRange = getDateRange(filters.selectedDay);

  // Fetch both variants to keep hooks order stable; choose based on restaurantId
  const {
    selectedCustomers: restaurantSelectedCustomers,
    selectedCustomersLoading: restaurantSelectedCustomersLoading,
    selectedCustomersError: restaurantSelectedCustomersError,
  } = useGetRestaurantSelectedFoodsCustomers(
    restaurantId,
    filters.search || undefined,
    dateRange.date_gte,
    dateRange.date_lte
  );

  const {
    selectedCustomers: generalSelectedCustomers,
    selectedCustomersLoading: generalSelectedCustomersLoading,
    selectedCustomersError: generalSelectedCustomersError,
  } = useGetSelectedFoodsCustomers(
    filters.search || undefined,
    dateRange.date_gte,
    dateRange.date_lte
  );

  const selectedCustomers = restaurantId ? restaurantSelectedCustomers : generalSelectedCustomers;
  const selectedCustomersLoading = restaurantId ? restaurantSelectedCustomersLoading : generalSelectedCustomersLoading;
  const selectedCustomersError = restaurantId ? restaurantSelectedCustomersError : generalSelectedCustomersError;

  // Helpers to compute plan group keys and labels for tabs
  const planGroupKey = useCallback((pg: PlanGroup) => `${pg?.plan_tag || ''}__${pg?.protein_size ?? ''}__${pg?.carb_size ?? ''}__${pg?.special_name || ''}` , []);

  const planGroupLabel = useCallback((pg: PlanGroup) => {
    const baseName = pg?.special_name && pg.special_name.length > 0 ? pg.special_name : (pg?.plan_tag || '');
    const proteinStr = typeof pg?.protein_size === 'number' ? `${pg.protein_size} P` : '-';
    const carbStr = typeof pg?.carb_size === 'number' ? `${pg.carb_size} C` : '-';
    const name = baseName && baseName.length > 0 ? baseName : (pg?.plan_tag || 'Unknown');
    return `${name} (${proteinStr}, ${carbStr})`;
  }, []);

  type PlanTab = { key: string; label: string; planTag?: string | null };

  const planTabs: PlanTab[] = React.useMemo(() => {
    const map = new Map<string, PlanTab>();
    if (selectedCustomers) {
      selectedCustomers.forEach(group => {
        group.data.forEach((pgd: CustomerData) => {
          const pg = pgd.plan_group;
          const key = planGroupKey(pg);
          if (!map.has(key)) {
            map.set(key, { key, label: planGroupLabel(pg), planTag: pg?.plan_tag || null });
          }
        });
      });
    }
    return [{ key: 'all', label: 'All' }, ...Array.from(map.values())];
  }, [selectedCustomers, planGroupKey, planGroupLabel]);

  const selectedTabValue = React.useMemo(() => {
    const v = filters.planTag;
    if (!v || v === 'all') return 'all';
    if (planTabs.some(t => t.key === v)) return v;
    const match = planTabs.find(t => t.planTag === v);
    return match ? match.key : 'all';
  }, [filters.planTag, planTabs]);

  // Custom search handler: when searching, default to 'All'
  const handleSearch = useCallback((value: string) => {
    if (value.length > 0) {
      onMultipleFilters({ search: value, planTag: 'all' });
    } else {
      onFilters('search', '');
    }
  }, [onFilters, onMultipleFilters]);

  // Ensure 'All' tab when search is active
  useEffect(() => {
    if (filters.search && filters.search.length > 0 && selectedTabValue !== 'all') {
      onFilters('planTag', 'all');
    }
  }, [filters.search, selectedTabValue, onFilters]);

  // Handle plan tag tab changes
  const handlePlanTagChange = (_event: React.SyntheticEvent, newValue: string) => {
    onFilters('planTag', newValue);
  };

  const {
    selectedFoodCustomers,
    selectedFoodCustomersLoading,
    selectedFoodCustomersError
  } = useGetSelectedFoodCustomerDetails(
    selectedCustomerDetails?.id || 0,
    selectedCustomerDetails?.date || ''
  );

  // Handle closing the customer details dialog
  const handleCloseDetails = () => {
    setSelectedCustomerDetails(null);
  };

  // Default to 'All' when not set
  useEffect(() => {
    if (!filters.planTag) {
      onFilters('planTag', 'all');
    }
  }, [filters.planTag, onFilters]);

  // Determine if a plan group is currently selected based on the selected tab
  const isGroupSelected = useCallback((pg: PlanGroup) => {
    if (selectedTabValue === 'all') return true;
    const key = planGroupKey(pg);
    if (key === selectedTabValue) return true;
    // Backward compatibility: raw plan_tag in URL
    if (filters.planTag && pg?.plan_tag && filters.planTag === pg.plan_tag) return true;
    return false;
  }, [filters.planTag, planGroupKey, selectedTabValue]);

  // Derive dynamic meal columns by unioning meals for selected groups
  const dynamicMealColumns = React.useMemo(() => {
    const map = new Map<string, string>();
    if (!selectedCustomers) return [] as string[];
    selectedCustomers.forEach(group => {
      group.data.forEach((planGroupData: CustomerData) => {
        const pg = planGroupData.plan_group;
        if (pg && isGroupSelected(pg)) {
          pg.subscriptions.forEach((sub: Subscription) => {
            sub.selected_foods.forEach((sf: SelectedFood) => {
              const key = sf.meal.toLowerCase();
              if (!map.has(key)) {
                map.set(key, sf.meal);
              }
            });
          });
        }
      });
    });
    return Array.from(map.keys()).sort();
  }, [selectedCustomers, isGroupSelected]);

  // Prepare data for display
  const prepareData = () => {
    // Wait for data
    if (!selectedCustomers) return [];

    // Create a map to group foods by subscription ID
    const subscriptionMap = new Map<string, {
      subscription: Subscription,
      planGroup: PlanGroup,
      date: string,
      foods: Map<string, string>
    }>();

    // Group foods by subscription
    selectedCustomers.forEach(group => {
      group.data.forEach((planGroupData: CustomerData) => {
        const pg = planGroupData.plan_group;
        if (!pg || !isGroupSelected(pg)) {
          return;
        }

        pg.subscriptions.forEach((subscription: Subscription) => {
          const key = `${group.date}-${subscription.id}`;

          if (!subscriptionMap.has(key)) {
            subscriptionMap.set(key, {
              subscription,
              planGroup: pg,
              date: group.date,
              foods: new Map<string, string>()
            });
          }

          // Add foods to the subscription, grouped by meal type
          subscription.selected_foods.forEach((selectedFood: SelectedFood) => {
            const entry = subscriptionMap.get(key)!;
            entry.foods.set(selectedFood.meal.toLowerCase(), selectedFood.food);
          });
        });
      });
    });

    // Convert map to array for display
    const flattenedData = Array.from(subscriptionMap.values()).map(({ subscription, planGroup, date, foods }) => {
      const dynamicFields = dynamicMealColumns.reduce((acc, key) => {
        acc[key] = foods.get(key) || '-';
        return acc;
      }, {} as Record<string, string>);
      const subscriptionObj: SubscriptionMealData = {
        id: subscription.id,
        small_id: subscription.small_id,
        phone_number: subscription.phone_number,
        delivery_time: `${subscription.delivery_time.start_time} - ${subscription.delivery_time.end_time}`,
        restaurant_note: subscription.restaurant_note || '',
        delivery_note: subscription.delivery_note || null,
        plan_protein_size: planGroup.protein_size,
        plan_carb_size: planGroup.carb_size,
        plan_tag: planGroup.plan_tag,
        ...dynamicFields
      };


      return { group: { date }, subscription: subscriptionObj };
    });

    // Filter by search term if provided
    const searchFiltered = filters.search
      ? flattenedData.filter(item => {
          const searchTerm = filters.search.toLowerCase();
          const sub = item.subscription;
          // Match static fields or any dynamic meal field
          const baseMatch = sub.small_id.toLowerCase().includes(searchTerm)
            || sub.phone_number.toLowerCase().includes(searchTerm)
            || (sub.restaurant_note && sub.restaurant_note.toLowerCase().includes(searchTerm))
            || (sub.delivery_note && sub.delivery_note.toLowerCase().includes(searchTerm));
          const mealMatch = dynamicMealColumns.some(key => (sub[key] as string).toLowerCase().includes(searchTerm));
          return baseMatch || mealMatch;
        })
      : flattenedData;

    const paginatedData = searchFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Group the paginated data by date for display
    const groupedData = paginatedData.reduce((acc: { [key: string]: { subscription: SubscriptionMealData, index: number }[] }, { group: { date }, subscription }, idx) => {
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push({ subscription, index: idx });
      return acc;
    }, {} as { [key: string]: { subscription: SubscriptionMealData, index: number }[] });

    return Object.entries(groupedData).map(([date, subscriptions]) => ({ date, subscriptions }));
  };

  // Calculate total count based on unique subscriptions
  const totalCount = React.useMemo(() => {
    if (!selectedCustomers) return 0;

    // Count unique subscriptions per date
    const uniqueSubscriptions = new Set<string>();

    selectedCustomers.forEach(group => {
      group.data.forEach((planGroupData: CustomerData) => {
        const pg = planGroupData.plan_group;
        if (!pg || !isGroupSelected(pg)) {
          return;
        }

        pg.subscriptions.forEach((subscription: Subscription) => {
          uniqueSubscriptions.add(`${group.date}-${subscription.id}`);
        });
      });
    });

    return uniqueSubscriptions.size;
  }, [selectedCustomers, isGroupSelected]);

  // Define columns for the table
  const columns: Column[] = [
    { id: 'small_id', label: 'ID', minWidth: 110 },
    { id: 'phone_number', label: 'Phone Number', minWidth: 120 },
    { id: 'size', label: 'Size', minWidth: 140, align: 'left' as const },
    // Dynamic meal columns
    ...dynamicMealColumns.map(key => ({
      id: key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      minWidth: 130,
      align: 'left' as const
    })),
    { id: 'restaurant_note', label: 'Restaurant Note', minWidth: 150, align: 'left' as const },
    { id: 'delivery_time', label: 'Delivery Time', minWidth: 120, align: 'center' as const }
  ];

  // Render table rows
  const renderRow = (item: { date: string; subscriptions: { subscription: SubscriptionMealData; index: number }[] }, _groupIndex: number) => {
    const { date } = item;
    return item.subscriptions.map((subscriptionItem, subscriptionIndex) => (
      <TableRow
        key={`${date}-${subscriptionItem.subscription.id}-${subscriptionItem.index}`}
        sx={{
          '& td': {
            borderBottom: subscriptionIndex === item.subscriptions.length - 1 ? '2px solid rgba(224, 224, 224, 1)' : undefined,
          },
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(145, 158, 171, 0.08)' }
        }}
        onClick={() => setSelectedCustomerDetails({ id: subscriptionItem.subscription.id, date })}
      >
        <TableCell>{subscriptionItem.subscription.small_id}</TableCell>
        <TableCell>{subscriptionItem.subscription.phone_number}</TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: 'text.primary'
            }}
          >
            {`${typeof subscriptionItem.subscription.plan_protein_size === 'number' ? `${subscriptionItem.subscription.plan_protein_size} P` : '-'}, ${typeof subscriptionItem.subscription.plan_carb_size === 'number' ? `${subscriptionItem.subscription.plan_carb_size} C` : '-'}`}
          </Typography>
        </TableCell>
        {dynamicMealColumns.map(key => (
          <TableCell key={key}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: subscriptionItem.subscription[key] !== '-' ? 500 : 400,
                color: subscriptionItem.subscription[key] !== '-' ? 'text.primary' : 'text.secondary'
              }}
            >
              {subscriptionItem.subscription[key]}
            </Typography>
          </TableCell>
        ))}
        <TableCell>
          <Typography
            variant="body2"
            sx={{
              fontWeight: subscriptionItem.subscription.restaurant_note && subscriptionItem.subscription.restaurant_note !== '' ? 500 : 400,
              color: subscriptionItem.subscription.restaurant_note && subscriptionItem.subscription.restaurant_note !== '' ? 'warning.main' : 'text.secondary'
            }}
          >
            {subscriptionItem.subscription.restaurant_note && subscriptionItem.subscription.restaurant_note !== '' ? subscriptionItem.subscription.restaurant_note : '-'}
          </Typography>
        </TableCell>
        <TableCell align="center">{subscriptionItem.subscription.delivery_time}</TableCell>
      </TableRow>
    ));
  };

  // Render dialog content based on loading and error states
  const renderDialogContent = () => {
    if (selectedFoodCustomersLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (selectedFoodCustomersError) {
      return (
        <Box sx={{ p: 3, color: 'error.main' }}>
          <Typography>Error loading customer details. Please try again.</Typography>
        </Box>
      );
    }

    if (!selectedFoodCustomers) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography>No details available for this customer.</Typography>
        </Box>
      );
    }

    // Cast to proper type with strict typing
    const customerData = selectedFoodCustomers as CustomerDetail;

    // Get plan group data for the selected customer
    const getPlanGroupData = () => {
      if (!selectedCustomerDetails || !selectedCustomers) return null;

      const group = selectedCustomers.find(g => g.date === selectedCustomerDetails.date);
      if (!group) return null;

      const planGroupData = group.data.find(data =>
        data.plan_group.subscriptions.some(sub => sub.id === selectedCustomerDetails.id)
      );

      return planGroupData?.plan_group || null;
    };

    const planGroup = getPlanGroupData();

    return (
      <Box sx={{ p: 2 }}>
        {/* Customer Information Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Customer Information
          </Typography>

          <Box sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: 'background.neutral',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ width: 140 }}>
                Name:
              </Typography>
              <Typography variant="body2">
                {customerData?.customer?.name || 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ width: 140 }}>
                Address:
              </Typography>
              <Typography variant="body2">
                {customerData?.customer?.area?.name_en ? `${customerData.customer.area.name_en}, ` : ''}
                Block: {customerData?.customer?.block || 'N/A'},
                Street: {customerData?.customer?.street || 'N/A'},
                House: {customerData?.customer?.house || 'N/A'}
                {customerData?.customer?.avenue && `, Avenue: ${customerData?.customer?.avenue}`}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ width: 140 }}>
                Delivery Time:
              </Typography>
              <Typography variant="body2">
                {selectedCustomerDetails && selectedCustomers?.find(g =>
                  g.date === selectedCustomerDetails.date
                )?.data.find(data =>
                  data.plan_group.subscriptions.some(sub => sub.id === selectedCustomerDetails.id)
                )?.plan_group.subscriptions.find(sub => sub.id === selectedCustomerDetails.id)
                  ?.delivery_time ?
                  `${selectedCustomers.find(g =>
                    g.date === selectedCustomerDetails.date
                  )?.data.find(data =>
                    data.plan_group.subscriptions.some(sub => sub.id === selectedCustomerDetails.id)
                  )?.plan_group.subscriptions.find(sub => sub.id === selectedCustomerDetails.id)
                    ?.delivery_time.start_time} - ${selectedCustomers.find(g =>
                    g.date === selectedCustomerDetails.date
                  )?.data.find(data =>
                    data.plan_group.subscriptions.some(sub => sub.id === selectedCustomerDetails.id)
                  )?.plan_group.subscriptions.find(sub => sub.id === selectedCustomerDetails.id)
                    ?.delivery_time.end_time}` : 'N/A'
                }
              </Typography>
            </Box>

            <Box sx={{ mt: 2, mb: 1, borderTop: '1px dashed', borderColor: 'divider' }} />

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ width: 140 }}>
                Plan Type:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {planGroup?.plan_tag || 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ width: 140 }}>
                Plan Protein:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {planGroup?.protein_size || 'N/A'} g
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ width: 140 }}>
                Plan Carbs:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {planGroup?.carb_size || 'N/A'} g
              </Typography>
            </Box>

            <Box sx={{ mt: 2, mb: 1, borderTop: '1px dashed', borderColor: 'divider' }} />

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ width: 140 }}>
                Restaurant Note:
              </Typography>
              <Typography variant="body2" color={customerData?.restaurant_note ? 'warning.main' : 'text.secondary'}>
                {customerData?.restaurant_note || '-'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ width: 140 }}>
                Delivery Note:
              </Typography>
              <Typography variant="body2" color={customerData?.delivery_note ? 'info.main' : 'text.secondary'}>
                {customerData?.delivery_note || '-'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Selected Food Section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Selected Food
          </Typography>

          <Box sx={{
            maxHeight: 400,
            overflow: 'auto',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            {customerData.selected_foods && customerData.selected_foods.length > 0 ? (
              customerData.selected_foods.map((food, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    borderBottom: index < customerData.selected_foods.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'rgba(145, 158, 171, 0.08)' }
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {food.food?.name_en || 'N/A'}
                    {food.meal && ` (${food.meal?.meal_tag?.name_en || 'N/A'})`}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No selected food found for this date.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  if (!user?.restaurants[0]?.id) {
    return <div>No restaurant found</div>;
  }

  return (
    <PermissionBasedGuard permissions={['partners:read:selectedfood']} hasContent>
      <Container maxWidth={false}>
        <CustomBreadcrumbs
          heading="Selected Food Customers"
          sx={{ mb: 2 }}
          links={[{}]}
        />

        <Card sx={{ mb: 3 }}>
          <TableToolbar
            filters={filters}
            onFilters={onFilters}
            onMultipleFilters={onMultipleFilters}
            onResetFilters={resetFilters}
            searchPlaceholder="Search phone number..."
            onSearch={handleSearch}
          />

          {planTabs.length > 0 && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tabs
                value={selectedTabValue}
                onChange={handlePlanTagChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="plan type tabs"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 120,
                    '&.Mui-selected': {
                      fontWeight: 'bold',
                    }
                  }
                }}
              >
                {planTabs.map((t) => (
                  <Tab
                    key={t.key}
                    label={t.label}
                    value={t.key}
                    icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stringToColor(t.label) }} />}
                    iconPosition="start"
                  />
                ))}
              </Tabs>
            </Box>
          )}
        </Card>

        <Card>
          <DataTable
            data={prepareData()}
            columns={columns}
            loading={selectedCustomersLoading}
            error={selectedCustomersError}
            page={page}
            rowsPerPage={rowsPerPage}
            count={totalCount}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            renderRow={renderRow}
          />
        </Card>

        {/* Customer Details Dialog */}
        <Dialog
          open={!!selectedCustomerDetails}
          onClose={handleCloseDetails}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              minHeight: '80vh',
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            pr: 6 // Space for close button
          }}>
            {!selectedFoodCustomersLoading && selectedFoodCustomers ? (
              <>
                <Typography component="span" variant="subtitle1" sx={{ mr: 1 }}>
                  Customer #{(selectedFoodCustomers as CustomerDetail)?.small_id || ''}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'background.neutral',
                    color: 'text.primary',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    ml: 'auto'
                  }}
                >
                  <Iconify icon="eva:phone-fill" sx={{ mr: 0.5, width: 16, height: 16 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 600
                    }}
                  >
                    {(selectedFoodCustomers as CustomerDetail)?.customer?.user?.phone_number || 'Unknown'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'background.neutral',
                    color: 'text.primary',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1
                  }}
                >
                  <Iconify icon="eva:calendar-fill" sx={{ mr: 0.5, width: 16, height: 16 }} />
                  <Typography variant="body2">
                    {selectedCustomerDetails?.date || ''}
                  </Typography>
                </Box>
              </>
            ) : (
              'Customer Details'
            )}
            <IconButton
              aria-label="close"
              onClick={handleCloseDetails}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {renderDialogContent()}
          </DialogContent>
        </Dialog>
      </Container>
    </PermissionBasedGuard>
  );
}

// Helper function to generate deterministic colors from strings
function stringToColor(string: string) {
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash * 32) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (Math.abs(hash) % 255);
    color += `00${value.toString(16)}`.slice(-2);
    hash = Math.floor(hash / 255);
  }
  return color;
}
